import { AppConfig, ChatBVProvider } from '@ltvco/refresh-lib/ctx';
import { Stack, Text, useMediaQuery } from '@ltvco/refresh-lib/theme';
import { RelatedReport } from '@ltvco/refresh-lib/v2';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useContext } from 'react';

import { constants } from 'appConstants';
import {
  ReportColumnContainer,
  EmailChatBvReport,
  PersonChatBvReport,
  VehicleChatBvReport,
  PropertyChatBVReport,
  PhoneChatBVReport,
  ObituaryChatBvReport,
  ContactChatBvReport,
  AncestorChatBvReport,
  UsernameChatBvReport,
} from 'components/ChatBV/components';
import { CHATBV_GA4_EVENTS, SEARCH_TYPES } from 'components/ChatBV/constants';
import {
  GenericReportResponse,
  OpenPersonReportInChatBVParams,
} from 'components/ChatBV/types';

type ReportColumnProps = {
  onClose: () => void;
  permalink?: string;
  type?: string;
  currentWidth: string;
  reportData?: GenericReportResponse;
  handleOpenReportInChatBV?: (message: string) => void;
  handleOpenPersonReportInChatBV?: (
    params: OpenPersonReportInChatBVParams
  ) => void;
  handleRelatedReportClick?: (relatedReport: RelatedReport) => void;
};

export const ReportColumn = ({
  onClose,
  permalink,
  type,
  currentWidth,
  reportData,
  handleOpenReportInChatBV,
  handleOpenPersonReportInChatBV,
  handleRelatedReportClick,
}: ReportColumnProps) => {
  const isMobileBreakpoint = useMediaQuery('(max-width: 1024px)');
  const { trackEventGA4 } = useContext(AppConfig);

  const getReportTitle = () => {
    switch (type) {
      case SEARCH_TYPES.PHONE:
        return 'Phone Report';
      case SEARCH_TYPES.PROPERTY:
        return 'Property Report';
      case SEARCH_TYPES.VEHICLE:
        return 'Vehicle Report';
      case SEARCH_TYPES.EMAIL:
        return 'Email Report';
      case SEARCH_TYPES.PERSON:
        return 'Person Report';
      case SEARCH_TYPES.OBITUARY:
        return 'Obituary Report';
      case SEARCH_TYPES.CONTACT:
        return 'Contact Report';
      case SEARCH_TYPES.ANCESTOR:
        return 'Ancestor Report';
      case SEARCH_TYPES.USERNAME:
        return 'Username Report';
      default:
        return 'Report';
    }
  };

  const renderReport = ({
    type: reportType,
    permalink: reportPermalink,
    reportData: reportDataProp,
  }: {
    type?: string;
    permalink?: string;
    reportData?: GenericReportResponse;
  }) => {
    if (!reportPermalink) {
      return null;
    }
    switch (reportType) {
      case SEARCH_TYPES.PHONE:
        return (
          <PhoneChatBVReport
            permalink={reportPermalink}
            reportData={reportDataProp}
          />
        );
      case SEARCH_TYPES.PROPERTY:
        return (
          <PropertyChatBVReport
            permalink={reportPermalink}
            reportData={reportDataProp}
          />
        );
      case SEARCH_TYPES.VEHICLE:
        return (
          <VehicleChatBvReport
            permalink={reportPermalink}
            reportData={reportDataProp}
          />
        );
      case SEARCH_TYPES.EMAIL:
        return (
          <EmailChatBvReport
            permalink={reportPermalink}
            reportData={reportDataProp}
          />
        );
      case SEARCH_TYPES.PERSON:
        return (
          <PersonChatBvReport
            permalink={reportPermalink}
            reportData={reportDataProp}
          />
        );
      case SEARCH_TYPES.OBITUARY:
        return <ObituaryChatBvReport permalink={reportPermalink} />;
      case SEARCH_TYPES.CONTACT:
        return <ContactChatBvReport permalink={reportPermalink} />;
      case SEARCH_TYPES.ANCESTOR:
        return <AncestorChatBvReport permalink={reportPermalink} />;
      case SEARCH_TYPES.USERNAME:
        return (
          <UsernameChatBvReport
            permalink={reportPermalink}
            reportData={reportDataProp}
          />
        );
      default:
        return null;
    }
  };

  const handleReportRedirect = () => {
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.report.type,
      subtype: CHATBV_GA4_EVENTS.report.subTypes.open_report_in_new_tab,
    });
    if (type && permalink) {
      const reportUrl = `${constants.links.baseUrl}/report/${type}?permalink=${permalink}`;
      window.open(reportUrl, '_blank');
    }
  };

  return (
    <>
      <ReportColumnContainer
        direction="column"
        spacing={2}
        minWidth={isMobileBreakpoint ? undefined : currentWidth}
        width={isMobileBreakpoint ? currentWidth : '40%'}
        height={isMobileBreakpoint ? '100%' : '98%'}
        data-testid="report-column-container"
        sx={{
          minWidth: 0,
          flexShrink: 0,
          ...(isMobileBreakpoint
            ? {
                flexGrow: 1,
                flexBasis: '100%',
                animation: 'fadeInScale 0.4s ease-out',
                '@keyframes fadeInScale': {
                  '0%': {
                    transform: 'scale(0.95)',
                    opacity: 0,
                  },
                  '100%': {
                    transform: 'scale(1)',
                    opacity: 1,
                  },
                },
              }
            : {
                animation: 'slideInFromRight 0.5s ease-out',
                '@keyframes slideInFromRight': {
                  '0%': {
                    transform: 'translateX(100%)',
                    opacity: 0,
                  },
                  '100%': {
                    transform: 'translateX(0)',
                    opacity: 1,
                  },
                },
              }),
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            position: 'sticky',
            top: 0,
            backgroundColor: 'background.paper',
            zIndex: 1,
            padding: '16px 20px',
          }}
        >
          <Text variant="body1" fontWeight="bold" color="text.primary">
            {getReportTitle()}
          </Text>
          <Stack direction="row" spacing={2}>
            <OpenInNewIcon
              sx={{ color: 'text.secondary', cursor: 'pointer' }}
              onClick={handleReportRedirect}
            />
            <CloseIcon
              onClick={onClose}
              sx={{ color: 'text.secondary', cursor: 'pointer' }}
            />
          </Stack>
        </Stack>
        <ChatBVProvider
          width="100%"
          openReportInChatBV={handleOpenReportInChatBV}
          openPersonReportInChatBV={handleOpenPersonReportInChatBV}
          openRelatedReportInChatBV={handleRelatedReportClick}
        >
          {renderReport({
            type,
            permalink,
            reportData,
          })}
        </ChatBVProvider>
      </ReportColumnContainer>
    </>
  );
};
